/**
 * Titres des exercices de SKILLCARDS (migration 0543) : la difficulté et le
 * découpage se lisent en PASTILLES à la fin du titre, plus en texte.
 *
 *   « Théorème de Pythagore 🟢 🎁 »   niveau 1, essai gratuit
 *   « Théorème de Pythagore 🟠 2️⃣ »   niveau 2, partie 2
 *   « Théorème de Pythagore 🔴 »      niveau 3, exercice resté entier
 *
 * Miroirs à garder synchro : `supabase/functions/_shared/prompts/card-exercise-generate.ts`
 * (génération) et la fonction SQL `split_card_exercise` (découpage, 0543).
 */

export type ExerciseTitleDifficulty = 'easy' | 'medium' | 'hard';

/** Pastille de difficulté (niveau 1 / 2 / 3). */
export const EXERCISE_LEVEL_MARK: Record<ExerciseTitleDifficulty, string> = {
  easy: '🟢',
  medium: '🟠',
  hard: '🔴',
};

/** Pastille de découpage, indicée par `split_part` (0 = essai gratuit, 1..3 = parties). */
export const EXERCISE_PART_MARK = ['🎁', '1️⃣', '2️⃣', '3️⃣'] as const;

const ALL_MARKS = [...Object.values(EXERCISE_LEVEL_MARK), ...EXERCISE_PART_MARK];

/** Anciennes mentions texte en fin de titre (avant 0543). */
const OLD_SUFFIX = /\s*·\s*(Niveau\s*[123]|Essai gratuit|Partie\s*[123])\s*$/u;

function stripOneMark(title: string): string | null {
  const t = title.trimEnd();
  for (const mark of ALL_MARKS) {
    if (t.endsWith(mark)) return t.slice(0, -mark.length).trimEnd();
  }
  const m = t.match(OLD_SUFFIX);
  return m ? t.slice(0, m.index).trimEnd() : null;
}

/** Titre sans ses pastilles ni ses anciennes mentions « · Niveau N / Essai gratuit / Partie N ». Pure. */
export function exerciseTitleBase(title: string): string {
  let t = title.trim();
  for (let guard = 0; guard < 6; guard += 1) {
    const next = stripOneMark(t);
    if (next === null) break;
    t = next;
  }
  return t;
}

/**
 * Titre avec ses pastilles : base + niveau (+ partie si l'exercice est
 * découpé). La base est d'abord nettoyée (jamais deux fois la même marque).
 * Pure.
 */
export function exerciseTitleWithMarks(
  base: string,
  opts: { difficulty?: ExerciseTitleDifficulty | null; splitPart?: number | null },
): string {
  const clean = exerciseTitleBase(base);
  const marks: string[] = [];
  if (opts.difficulty) marks.push(EXERCISE_LEVEL_MARK[opts.difficulty]);
  if (opts.splitPart != null && opts.splitPart >= 0 && opts.splitPart <= 3) {
    marks.push(EXERCISE_PART_MARK[opts.splitPart]!);
  }
  return marks.length ? `${clean} ${marks.join(' ')}` : clean;
}

/** Retire seulement la pastille de partie (ou l'ancienne mention) en fin de titre. Pure. */
export function stripExercisePartMark(title: string): string {
  const t = title.trimEnd();
  for (const mark of EXERCISE_PART_MARK) {
    if (t.endsWith(mark)) return t.slice(0, -mark.length).trimEnd();
  }
  return t.replace(/\s*·\s*(Essai gratuit|Partie\s*[123])\s*$/u, '');
}

/** Retire seulement la pastille de niveau (ou l'ancienne mention) en fin de titre. Pure. */
export function stripExerciseLevelMark(title: string): string {
  const t = title.trimEnd();
  for (const mark of Object.values(EXERCISE_LEVEL_MARK)) {
    if (t.endsWith(mark)) return t.slice(0, -mark.length).trimEnd();
  }
  return t.replace(/\s*·\s*Niveau\s*[123]\s*$/u, '');
}
